/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Typography, Dialog, DialogTitle, IconButton } from '@mui/material';
import { X } from '@jda/lui-common-icon-library-mui5';
import ManageLocationsDialogShell from './ManageLocationsDialogShell';
import ManageLocationsDialogContent, { ManageLocationsDialogContentProps } from './ManageLocationsDialogContent';
import { Location } from '../../models/Locations/Locations';
import { MANAGE_LOCATIONS, CLOSE } from './constants';
import { useStyles } from './styles';
import { fetchLocationTypes } from '../../store/locations/locationTypes';
import Loader from '../Loader';

interface IManageLocationsDialogProps
  extends Pick<
    ManageLocationsDialogContentProps,
    'maxToAdd' | 'maxToRemove' | 'disableCurrentLocations' | 'isLocationDisabled' | 'onSave' | 'onClose'
  > {
  currentLocationsLoader?: Promise<Location[]>;
  title?: string;
}

const ManageLocationsDialog = ({
  currentLocationsLoader,
  title = MANAGE_LOCATIONS,
  onClose,
  ...otherProps
}: IManageLocationsDialogProps) => {
  const [currentLocations, setCurrentLocations] = useState<Location[]>([]);
  const [areCurrentLocationsLoading, setAreCurrentLocationsLoading] = useState(Boolean(currentLocationsLoader));
  const dispatch = useDispatch();
  const { classes } = useStyles();

  useEffect(() => {
    fetchLocationTypes(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (!currentLocationsLoader) {
      return;
    }

    setAreCurrentLocationsLoading(true);
    currentLocationsLoader.then(setCurrentLocations).finally(() => {
      setAreCurrentLocationsLoading(false);
    });
  }, [currentLocationsLoader]);

  return (
    <Dialog open={true} fullWidth={true} maxWidth="md" aria-labelledby="manage-locations-dialog-title">
      <DialogTitle id="manage-locations-dialog-title" className={classes.dialogTitle}>
        <Typography variant="h4" className={classes.dialogTitleText}>
          {title}
        </Typography>
        <IconButton aria-label={CLOSE} size="small" onClick={onClose}>
          <X />
        </IconButton>
      </DialogTitle>
      {areCurrentLocationsLoading ? (
        <ManageLocationsDialogShell isSaveDisabled={true} onClose={onClose}>
          <Loader showLoader={true} isCenter={true} />
        </ManageLocationsDialogShell>
      ) : (
        <ManageLocationsDialogContent currentLocations={currentLocations} onClose={onClose} {...otherProps} />
      )}
    </Dialog>
  );
};

export default ManageLocationsDialog;
